import React, { Component } from 'react'
import { toast } from 'react-toastify'
import {
    checkSanitationNow,
    completeSanitation,
    getSanitationPolicy,
    getSanitationStatus,
    SanitationPolicyResponse,
    SanitationStatusResponse,
    setSanitationPolicy,
} from '../../../api/0x0005/general-settings'
import { getString } from '../../../locale'

type Props = {
    clientId: string
}

type State = {
    loading: boolean
    busy: boolean
    status?: SanitationStatusResponse
    policy?: SanitationPolicyResponse
}

const STATUS_LABELS: Record<number, string> = {
    0: 'sanitation.status_waiting_for_sync',
    1: 'sanitation.status_compliant',
    2: 'sanitation.status_due_soon',
    3: 'sanitation.status_overdue',
}

export default class SanitationTab extends Component<Props, State> {
    private pollTimer?: ReturnType<typeof setInterval>

    state: State = {
        loading: true,
        busy: false,
    }

    componentDidMount = async () => {
        await this.load()
        this.pollTimer = setInterval(() => {
            this.loadStatusOnly().catch(() => undefined)
        }, 30000)
    }

    componentWillUnmount(): void {
        if (this.pollTimer) clearInterval(this.pollTimer)
    }

    load = async () => {
        try {
            this.setState({ loading: true })
            const [status, policy] = await Promise.all([
                getSanitationStatus(this.props.clientId),
                getSanitationPolicy(this.props.clientId),
            ])
            this.setState({ status, policy })
        } catch (error: any) {
            toast.error(error?.message || getString('sanitation.load_error'))
        } finally {
            this.setState({ loading: false })
        }
    }

    loadStatusOnly = async () => {
        const status = await getSanitationStatus(this.props.clientId)
        this.setState({ status })
    }

    checkNow = async () => {
        try {
            this.setState({ busy: true })
            const response = await checkSanitationNow(this.props.clientId)
            if (response.success === 1) {
                toast.success(response.message || getString('sanitation.check_success'))
                await this.load()
            } else {
                toast.error(response.message || getString('sanitation.check_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('sanitation.check_error'))
        } finally {
            this.setState({ busy: false })
        }
    }

    completeNow = async () => {
        try {
            this.setState({ busy: true })
            const response = await completeSanitation(this.props.clientId)
            if (response.success === 1) {
                toast.success(response.message || getString('sanitation.complete_success'))
                await this.load()
            } else {
                toast.error(response.message || getString('sanitation.complete_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('sanitation.complete_error'))
        } finally {
            this.setState({ busy: false })
        }
    }

    savePolicy = async () => {
        const intervalDays = this.state.policy?.intervalDays ?? 90
        const enabled = this.state.policy?.enabled ?? 1
        if (intervalDays < 30 || intervalDays > 3650) {
            toast.error(getString('sanitation.interval_range'))
            return
        }

        try {
            this.setState({ busy: true })
            const response = await setSanitationPolicy(this.props.clientId, enabled, intervalDays)
            if (response.success === 1) {
                toast.success(response.message || getString('sanitation.policy_saved'))
                await this.load()
            } else {
                toast.error(response.message || getString('sanitation.policy_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('sanitation.policy_error'))
        } finally {
            this.setState({ busy: false })
        }
    }

    setEnabled = async (enabled: number) => {
        const intervalDays = this.state.policy?.intervalDays ?? 90
        try {
            this.setState({
                busy: true,
                policy: {
                    enabled,
                    intervalDays,
                },
            })
            const response = await setSanitationPolicy(this.props.clientId, enabled, intervalDays)
            if (response.success === 1) {
                toast.success(response.message || getString('sanitation.policy_saved'))
                await this.load()
            } else {
                toast.error(response.message || getString('sanitation.policy_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('sanitation.policy_error'))
        } finally {
            this.setState({ busy: false })
        }
    }

    formatEpoch = (seconds: number): string => {
        if (!seconds) return '-'
        const date = new Date(seconds * 1000)
        return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString()
    }

    render() {
        const { loading, busy, status, policy } = this.state
        if (loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg text-gray-600 dark:text-gray-400">{getString('common.loading')}</div>
                </div>
            )
        }

        return (
            <div className="p-6 space-y-6 text-gray-800 dark:text-gray-100">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{getString('sanitation.title')}</h1>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 space-y-3 text-sm text-gray-800 dark:text-gray-100">
                    <div>{getString('sanitation.status')}: {status?.enabled === 0 ? getString('sanitation.status_disabled') : getString((STATUS_LABELS[status?.statusCode ?? 0] as any) || 'sanitation.status_waiting_for_sync')}</div>
                    <div>{getString('sanitation.enabled')}: {status?.enabled === 1 ? getString('license.yes') : getString('license.no')}</div>
                    <div>{getString('sanitation.can_start_new_pour')}: {status?.canStartNewPour === 1 ? getString('license.yes') : getString('license.no')}</div>
                    <div>{getString('sanitation.ntp_synced_once')}: {status?.ntpSyncedOnce === 1 ? getString('license.yes') : getString('license.no')}</div>
                    <div>{getString('sanitation.interval_days')}: {status?.intervalDays ?? 0}</div>
                    <div>{getString('sanitation.warning_days')}: {status?.warningDays ?? 0}</div>
                    <div>{getString('sanitation.seconds_until_due')}: {status?.secondsUntilDue ?? 0}</div>
                    <div>{getString('sanitation.last_completed')}: {this.formatEpoch(status?.lastCompletedEpochSec ?? 0)}</div>
                    <div>{getString('sanitation.next_due')}: {this.formatEpoch(status?.nextDueEpochSec ?? 0)}</div>
                    <div>{getString('sanitation.last_check')}: {this.formatEpoch(status?.lastCheckEpochSec ?? 0)}</div>
                    {status?.message && <div>{getString('sanitation.message')}: {status.message}</div>}
                    <div className="flex gap-2 pt-2">
                        <button
                            onClick={this.load}
                            disabled={busy}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-70"
                        >
                            {getString('license.refresh')}
                        </button>
                        <button
                            onClick={this.checkNow}
                            disabled={busy}
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded disabled:opacity-70"
                        >
                            {getString('sanitation.check_now')}
                        </button>
                        <button
                            onClick={this.completeNow}
                            disabled={busy}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-70"
                        >
                            {getString('sanitation.complete_now')}
                        </button>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 space-y-3 text-gray-800 dark:text-gray-100">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{getString('sanitation.policy')}</h2>
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <input
                            type="checkbox"
                            checked={(policy?.enabled ?? 1) === 1}
                            onChange={event => this.setEnabled(event.target.checked ? 1 : 0)}
                            disabled={busy}
                        />
                        <span>{getString('sanitation.enabled')}</span>
                    </label>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">{getString('sanitation.interval_days')}</label>
                    <input
                        type="number"
                        min={30}
                        max={3650}
                        value={policy?.intervalDays ?? 90}
                        onChange={event =>
                            this.setState({
                                policy: {
                                    enabled: this.state.policy?.enabled ?? 1,
                                    intervalDays: Number.parseInt(event.target.value, 10) || 90,
                                },
                            })
                        }
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                    />
                    <p className="text-xs text-gray-600 dark:text-gray-400">{getString('sanitation.interval_hint')}</p>
                    <button
                        onClick={this.savePolicy}
                        disabled={busy}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-70"
                    >
                        {getString('common.save')}
                    </button>
                </div>
            </div>
        )
    }
}
